/**
 * L'écoute en continu (lots 2 et 3 de la voix fluide, core-ai D47).
 *
 * Le son du micro part au fil de l'eau vers `/runtime/ecoute` ; le texte et les
 * tours de parole reviennent. Le widget n'a plus besoin d'un clic pour savoir
 * que le visiteur a fini : Voxtral le détecte, sans couper une hésitation.
 *
 * Capture par `ScriptProcessorNode` et non `AudioWorklet` : un worklet se charge
 * depuis une URL de module, que la politique de sécurité (CSP) des sites de nos
 * clients peut interdire. Le widget doit marcher partout où il est posé.
 */

/** Ce qu'attend Voxtral Realtime : PCM 16 bits, mono, 16 kHz. */
export const FREQUENCE_ECOUTE = 16_000;

/**
 * Ramène le son du micro (souvent 48 kHz) à 16 kHz, par interpolation linéaire,
 * sans couture entre deux blocs : la position de lecture et le dernier
 * échantillon sont gardés d'un bloc au suivant.
 */
export class Reechantillonneur {
  private position = 0;
  private dernier = 0;

  constructor(
    private readonly source: number,
    private readonly cible = FREQUENCE_ECOUTE
  ) {}

  traiter(entree: Float32Array): Int16Array {
    const pas = this.source / this.cible;
    const n = entree.length;
    if (!n) return new Int16Array(0);
    const sortie = new Int16Array(Math.ceil((n - this.position) / pas) + 1);
    let k = 0;
    let t = this.position;
    while (t <= n - 1) {
      const i = Math.floor(t);
      const f = t - i;
      const a = i < 0 ? this.dernier : (entree[i] as number);
      const b = i + 1 < n ? (entree[i + 1] as number) : a;
      const v = Math.max(-1, Math.min(1, a + (b - a) * f));
      sortie[k++] = Math.round(v * 32767);
      t += pas;
    }
    this.position = t - n;
    this.dernier = entree[n - 1] as number;
    return sortie.subarray(0, k);
  }
}

const mots = (texte: string) =>
  texte
    .toLowerCase()
    .split(/[^\p{L}\p{N}]+/u)
    .filter((m) => m.length > 1);

/** Nombre de mots porteurs d'un texte entendu. */
export function compterMots(texte: string): number {
  return mots(texte).length;
}

/**
 * Ce que le micro entend est-il l'écho de ce que dit l'agent ?
 *
 * L'annulation d'écho du navigateur retire l'essentiel de la voix de l'agent,
 * mais pas tout sur des haut-parleurs de portable. Sans ce filtre, l'agent
 * s'entendrait, s'interromprait et se répondrait à lui-même (Pipecat, Picovoice :
 * « l'auto-interruption est la panne type »). Écho si la plupart des mots
 * entendus figurent dans ce que l'agent est en train de dire.
 */
export function estEcho(entendu: string, dit: string): boolean {
  const entendus = mots(entendu);
  if (!entendus.length) return true;
  const ditsEnsemble = new Set(mots(dit));
  if (!ditsEnsemble.size) return false;
  const communs = entendus.filter((m) => ditsEnsemble.has(m)).length;
  return communs / entendus.length >= 0.6;
}

export type EtatTour = 'debut' | 'fin_probable' | 'reprise' | 'fin';

export type RappelsEcoute = {
  /** La chaîne jusqu'à Voxtral est ouverte : le visiteur peut parler. */
  pret: () => void;
  texte: (morceau: string) => void;
  tour: (etat: EtatTour) => void;
  /** L'écoute est finie. `raison` : le visiteur, le service (plafond, panne). */
  fin: (raison: 'visiteur' | 'service' | 'refus') => void;
};

export class EcouteEnFlux {
  private socket: WebSocket | null = null;
  private flux: MediaStream | null = null;
  private contexte: AudioContext | null = null;
  private noeuds: AudioNode[] = [];
  private attente: ArrayBuffer[] = [];
  private finie = false;

  static disponible(): boolean {
    return (
      typeof window !== 'undefined' &&
      window.isSecureContext &&
      Boolean(navigator.mediaDevices?.getUserMedia) &&
      typeof WebSocket !== 'undefined' &&
      typeof (window.AudioContext ||
        (window as unknown as { webkitAudioContext?: unknown }).webkitAudioContext) !== 'undefined'
    );
  }

  /**
   * Ouvre le micro (la permission est demandée la première fois, et un refus
   * lève), puis la WebSocket. L'audio capturé avant l'ouverture est gardé.
   */
  async demarrer(url: string, rappels: RappelsEcoute): Promise<void> {
    this.flux = await navigator.mediaDevices.getUserMedia({
      // L'annulation d'écho est ce qui permet d'interrompre l'agent sans qu'il
      // s'entende lui-même (lot 3).
      audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
    });

    const Contexte =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    const contexte = new Contexte();
    this.contexte = contexte;
    if (contexte.state === 'suspended') void contexte.resume();

    const reech = new Reechantillonneur(contexte.sampleRate);
    const source = contexte.createMediaStreamSource(this.flux);
    const capture = contexte.createScriptProcessor(4096, 1, 1);
    // Un ScriptProcessor ne tourne que relié à la sortie : on le relie par un
    // gain nul, pour ne rien rejouer du micro dans les haut-parleurs.
    const muet = contexte.createGain();
    muet.gain.value = 0;
    capture.onaudioprocess = (e) => {
      if (this.finie) return;
      const pcm = reech.traiter(e.inputBuffer.getChannelData(0));
      if (!pcm.length) return;
      const tampon = pcm.slice().buffer;
      if (this.socket?.readyState === WebSocket.OPEN) this.socket.send(tampon);
      else this.attente.push(tampon);
    };
    source.connect(capture);
    capture.connect(muet);
    muet.connect(contexte.destination);
    this.noeuds = [source, capture, muet];

    const socket = new WebSocket(url);
    socket.binaryType = 'arraybuffer';
    this.socket = socket;
    let ouverte = false;
    socket.onopen = () => {
      ouverte = true;
      for (const t of this.attente.splice(0)) socket.send(t);
    };
    socket.onmessage = (e) => {
      if (typeof e.data !== 'string') return;
      let message: { type?: string; texte?: unknown; etat?: unknown };
      try {
        message = JSON.parse(e.data) as typeof message;
      } catch {
        return;
      }
      if (message.type === 'pret') rappels.pret();
      else if (message.type === 'texte' && typeof message.texte === 'string') rappels.texte(message.texte);
      else if (message.type === 'tour' && typeof message.etat === 'string') rappels.tour(message.etat as EtatTour);
    };
    socket.onclose = () => {
      const parVisiteur = this.finie;
      this.liberer();
      rappels.fin(parVisiteur ? 'visiteur' : ouverte ? 'service' : 'refus');
    };
  }

  /** Le visiteur coupe : fin propre (la dernière phrase est encore transcrite). */
  arreter(): void {
    if (this.finie) return;
    this.finie = true;
    const socket = this.socket;
    if (socket?.readyState === WebSocket.OPEN) {
      socket.send(JSON.stringify({ type: 'fin' }));
      // Le serveur ferme après `termine` ; sinon, on ferme nous-mêmes.
      setTimeout(() => socket.readyState === WebSocket.OPEN && socket.close(), 3000);
    } else {
      socket?.close();
    }
    this.liberer();
  }

  /** Rend le micro au système : le voyant d'enregistrement doit s'éteindre. */
  private liberer(): void {
    this.flux?.getTracks().forEach((piste) => piste.stop());
    this.flux = null;
    for (const n of this.noeuds) {
      try {
        n.disconnect();
      } catch {
        /* déjà déconnecté */
      }
    }
    this.noeuds = [];
    void this.contexte?.close().catch(() => undefined);
    this.contexte = null;
    this.attente = [];
  }
}
